import { ColumnDef } from '@tanstack/react-table';

import { Grid } from '@/components/core/Grid';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useDataModel } from '@/hooks/useDataModel';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslation } from '@/hooks/useTranslation';

import { AttributeData } from './ItemHistoryGrid';
import {
    attributeNameForHistoryLabel,
    extractAllDynamicKeys,
} from './utils';

interface ItemHistoryAttributesProps {
    data: { history: AttributeData[] }
}

const ItemHistoryAttributes = ({ data }: ItemHistoryAttributesProps) => {
    const { t } = useTranslation();
    const { dataModel } = useDataModel();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { formatDate } = useDateFormatter();

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'updatedAt__',
            header: t('HISTORY.MODIFIED_WHEN'),
            cell: (info) => {
                const value = info.getValue();
                return value ? formatDate(value as number, 'historyDateTime') : '';
            },
            sortingFn: (rowA:any, rowB:any, columnId) => {
                const valueA = rowA.getValue(columnId);
                const valueB = rowB.getValue(columnId);
                if (!valueA && !valueB) return 0;
                if (!valueA) return 1;
                if (!valueB) return -1;
                return new Date(valueA).getTime() - new Date(valueB).getTime();
            },
            enableResizing: true,
        },
        {
            accessorKey: 'updatedBy__',
            header: t('HISTORY.MODIFIED_BY'),
            enableResizing: true,
        },
        {
            accessorKey: 'attributeLabel',
            header: t('HISTORY.ATTRIBUTE_LABEL'),
            enableResizing: true,
            cell: (info) => info.row.original.attributeLabel,
        },
        {
            accessorKey: 'attributeElement',
            header: t('HISTORY.ATTRIBUTE_ELEMENT'),
            enableResizing: true,
        },
        {
            accessorKey: 'oldValue__',
            header: t('HISTORY.OLD_VALUE'),
            enableResizing: true,
        },
        {
            accessorKey: 'currentValue__',
            header: t('HISTORY.NEW_VALUE'),
            enableResizing: true,
        },
    ];

    const transformObjects = (itemsArray: AttributeData[]) => {
        const result: any[] = [];

        itemsArray.forEach((item: any) => {
            const baseItem = {
                updatedAt__: item.updatedAt__ || '',
                updatedBy__: item.updatedBy__ ? t(item.updatedBy__): '',
            };

            const dynamicKeys = extractAllDynamicKeys(item);

            if (dynamicKeys.length > 0) {
                dynamicKeys.forEach((dynamicKey) => {
                    const nameForLabel = attributeNameForHistoryLabel(dynamicKey);
                    const attribute = dataModel?.attribute(nameForLabel);
                    const attributeLabel = attribute
                        ? translateAttributeLabel(attribute)
                        : nameForLabel;

                    result.push({
                        ...baseItem,
                        attributeLabel: attributeLabel,
                        attributeElement: dynamicKey,
                        oldValue__: item[dynamicKey]?.oldValue__,
                        currentValue__: item[dynamicKey]?.currentValue__,
                    });
                });
            } else {
                result.push(baseItem);
            }
        });

        return result;
    };

    return (
        <div id="timeline-history-grid" className="history-grid tw-flex tw-flex-col tw-h-full">
            <div className="!tw-h-full !tw-min-h-[70vh] tw-flex tw-flex-col">
                <Grid
                    burgerMenu={true}
                    isWrapContent={true}
                    storedStateKey={'Items-history-attributes'}
                    insideItemEditor={true}
                    columns={columns}
                    data={transformObjects(data.history)}
                    enableSorting={true}
                    className='tw-basis-[35rem] !tw-h-full !tw-min-h-[70vh]' />
            </div>
        </div>
    );
};

export default ItemHistoryAttributes;
